import { Observable, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { AuthService } from './services/auth.service';
import { routerSegments } from './consts/routes';
import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  importProvidersFrom,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeHe from '@angular/common/locales/he';
import localeJa from '@angular/common/locales/ja';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Directionality } from '@angular/cdk/bidi';
import {
  providePublicAuthConfig,
  UNIVERSAL_LOGIN_CONFIG,
  AuthRedirectLoopService,
  buildAccessDeniedRedirectUrl,
  clearCurrentUrlQueryParams,
  UniversalLoginService,
} from '@auth';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideShareAuthSession } from '@share-auth-session';
import { provideShareChat } from '@share-chat';
import { ShareUserAccountSettingsModule } from '@share-user-account-settings';
import { CustomDirectionality, provideUiTranslations } from '@ui';
import { provideToastr } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { api, userManagerURL } from './consts/url';
import { UserSettingsService } from './services/user-settings.service';
import { AuthActions } from './store/state/auth/auth.actions';
import { authenticationInterceptor } from './services/authentication-interceptor.service';
import {
  ChatSharedAuthSessionManager,
  ChatInvalidSessionHandler,
} from './services/shared-auth-session.service';
import { ChatAdapterActions } from './store/state/chat/chat.actions';
import { SharedActions } from './store/state/shared/shared.actions';
import { OrganizationActions } from './store/state/organization/organization.actions';
import {
  selectActiveOrganizationChatStatus,
  selectActiveOrganizationSynapseSpaceId,
  selectHasAvailableChatOrganization,
  selectUserOrganizationCount,
  selectArchiveDurationLimit,
} from './store/state/organization/organization.selectors';
import { provideAppState } from './store/app.state.config';
import { userFeature } from './store/state/user/user.feature';
import { userSettingsFeature } from './store/state/user-settings/user-settings.feature';
import { permissionsFeature } from './store/state/permissions/permissions.feature';
// LOCALE_ID follows the UI language, so every language with an i18n file needs its locale data
// ('en' ships with Angular); otherwise DatePipe throws NG0701.
registerLocaleData(localeEs);
registerLocaleData(localeHe);
registerLocaleData(localeJa);

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function initApp(): Observable<unknown> {
  const store = inject(Store);
  const actions$ = inject(Actions);
  const authService = inject(AuthService);
  const universalLoginService = inject(UniversalLoginService);
  const authRedirectLoopService = inject(AuthRedirectLoopService);

  return new Observable<unknown>((subscriber) => {
    // Subscribe FIRST so we can't miss the success action
    const sub = actions$
      .pipe(
        ofType(AuthActions.requiredDataLoadedSuccess, AuthActions.logout),
        take(1),
      )
      .subscribe({
        next: () => {
          subscriber.next(void 0);
          subscriber.complete();
        },
        error: (err) => subscriber.error(err),
      });

    const currentUrl = new URL(window.location.href);
    const error = currentUrl.searchParams.get('error');
    const errorDescription = currentUrl.searchParams.get('error_description');

    if (
      currentUrl.pathname === `/${routerSegments.emailVerification}` &&
      currentUrl.searchParams.get('success') === 'true'
    ) {
      clearCurrentUrlQueryParams(currentUrl);
      universalLoginService.redirectToLogin(window.location.origin);
      subscriber.next(void 0);
      subscriber.complete();
      return () => sub.unsubscribe();
    }

    if (error === 'access_denied') {
      const redirectUrl = buildAccessDeniedRedirectUrl({
        error,
        errorDescription,
        resetPasswordPath: `/${routerSegments.resetPassword}`,
        emailVerificationPath: `/${routerSegments.emailVerification}`,
        accessDeniedPath: `/${routerSegments.accessDenied}`,
      });

      if (redirectUrl) {
        clearCurrentUrlQueryParams(currentUrl);
        window.location.href = redirectUrl;
        subscriber.next(void 0);
        subscriber.complete();
        return () => sub.unsubscribe();
      }

      authService.logout(errorDescription ?? error);
      subscriber.next(void 0);
      subscriber.complete();
      return () => sub.unsubscribe();
    }

    if (authService.isLogoutCallbackRoute(currentUrl)) {
      authRedirectLoopService.reset();
      authService.handleLogoutCallback(currentUrl);
      subscriber.next(void 0);
      subscriber.complete();
      return () => sub.unsubscribe();
    }

    const code = currentUrl.searchParams.get('code');

    if (code && isRootRoute(currentUrl.pathname)) {
      authRedirectLoopService.reset();
      currentUrl.searchParams.delete('code');
      currentUrl.searchParams.delete('state');
      currentUrl.searchParams.delete('orgId');
      window.history.replaceState({}, '', currentUrl.toString());
      store.dispatch(AuthActions.socialLoginByCode({ code }));
      return () => sub.unsubscribe();
    }

    // Then do the bootstrap logic (dispatches / navigation)
    void (async () => {
      let isLoggedIn = authService.isLoggedIn();

      if (!isLoggedIn) {
        isLoggedIn = await authService.ensureValidSession({
          allowBackgroundRefresh: false,
          isSilentRefresh: false,
        });
      }

      if (!isLoggedIn) {
        store.dispatch(AuthActions.requiredDataLoadedSuccess());
      } else {
        authRedirectLoopService.reset();
        store.dispatch(AuthActions.startLoadRequiredData());
      }
    })();

    return () => sub.unsubscribe();
  });
}

function isRootRoute(pathname: string): boolean {
  return pathname === '' || pathname === '/';
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptors([authenticationInterceptor])),
    provideToastr(),
    {
      provide: LOCALE_ID,
      deps: [TranslateService],
      useFactory: (translate: TranslateService) =>
        translate.getCurrentLang() || 'en-US',
    },
    providePublicAuthConfig({
      auth0Domain: environment.auth0.domain,
      auth0ClientId: environment.auth0.clientID,
      authStartUrl: api.authV2.login,
      logoutPublicUrl: api.authV2.logoutPublic,
      resendVerificationEmailPublicUrl:
        api.authV2.resendVerificationEmailPublic,
    }),
    {
      provide: UNIVERSAL_LOGIN_CONFIG,
      useValue: {
        webUrl: window.location.origin,
        defaultRedirectUrl: window.location.origin,
        accessDeniedPath: `/${routerSegments.accessDenied}`,
      },
    },
    ...provideShareAuthSession({
      sessionManager: ChatSharedAuthSessionManager,
      invalidSessionHandler: ChatInvalidSessionHandler,
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        fallbackLang: 'en',
        extend: true,
      }),
    ),
    ...provideUiTranslations(),
    { provide: Directionality, useExisting: CustomDirectionality },
    importProvidersFrom(
      provideShareChat({
        config: {
          apiUrl: environment.apiLumana,
          vmsUrl: environment.apps.webUrl,
          env: environment.env,
          thumbnailsApiUrl: environment.roundRobinUrl,
        },
        authentication: {
          actions: { logoutSuccess: AuthActions.logout },
        },
        user: {
          selectors: {
            settings: userSettingsFeature.selectUserSettings,
            isDeveloper: userFeature.selectIsDeveloper,
            permissions: permissionsFeature.selectPermissionsState,
            timezone: userFeature.selectTimezone,
          },
          actions: {
            updateProfileSuccess: ChatAdapterActions.updateProfileSuccess,
          },
        },
        organization: {
          selectors: {
            selectActiveOrganizationChatStatus:
              selectActiveOrganizationChatStatus,
            selectActiveOrganizationSynapseSpaceId:
              selectActiveOrganizationSynapseSpaceId,
            selectUserOrganizationCount,
            selectArchiveDurationLimit,
            selectHasAvailableChatOrganization,
          },
          actions: {
            getActiveOrganization: OrganizationActions.getActiveOrganization,
            retryActiveOrganization: AuthActions.retryOrganizationDataLoad,
            setActiveOrganization: ChatAdapterActions.setActiveOrganization,
            updateActiveOrganizationSuccess:
              ChatAdapterActions.updateActiveOrganizationSuccess,
          },
        },
        shared: { actions: { showMessage: SharedActions.showMessage } },
      }),
    ),
    // NgModule variant: the root store here is StoreModule.forRoot (share-chat needs StoreRootModule),
    // and provideState/provideEffects require the standalone root store provider.
    importProvidersFrom(
      ShareUserAccountSettingsModule.forRoot({
        apiUrl: environment.apiLumana,
        userManagerUrl: userManagerURL,
      }),
    ),
    UserSettingsService,
    provideAppState(),
    provideAppInitializer(initApp),
  ],
};
